import { Component, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { LmsDsManualEntryService } from "./lms-ds-manual-entry.service";

type FieldType = "text" | "date" | "file";

interface FieldDef {
  key: string;
  type: FieldType;
}

interface FormDef {
  allowMultiple: boolean;   // multiple objects in new_data
  fields: FieldDef[];
}


@Component({
  selector: 'app-lms-ds-manual-entry',
  // standalone: true,
  // imports: [],
  templateUrl: './lms-ds-manual-entry.component.html',
  styleUrl: './lms-ds-manual-entry.component.scss'
})
export class LmsDsManualEntryComponent implements OnInit {
  public Editor = ClassicEditor;
  // dropdown 1
  whichFieldOptions: any[] = [];
  selectedWhichField: string | null = null;

  // molecule section (already in your file)
  searchValue = "";
  submolecules: any[] = [];
  selectedSubMolecule: any;

  // dynamic form
  manualForm!: FormGroup;

  // store current form definition
  currentDef: FormDef | null = null;

  // ✅ Configuration for each whichField value (exactly as you gave)
  private formMap: Record<string, FormDef> = {
    "Approved ANDA filers": {
      allowMultiple: false,
      fields: [
        { key: "ANDA No", type: "text" },
        { key: "Drug Name", type: "text" },
        { key: "Active Ingredients", type: "text" },
        { key: "Strength", type: "text" },
        { key: "Dosage Form/Route", type: "text" },
        { key: "Marketing Status", type: "text" },
        { key: "Approval Date", type: "date" },
        { key: "Applicant Holder", type: "text" },
        { key: "TE Code", type: "text" },
        { key: "RLD", type: "text" },
        { key: "RS", type: "text" },
      ],
    },

    "Tentatively approved ANDAs": {
      allowMultiple: false,
      fields: [
        { key: "ANDA No", type: "text" },
        { key: "Drug Name", type: "text" },
        { key: "Active Ingredients", type: "text" },
        { key: "Strength", type: "text" },
        { key: "Dosage Form/Route", type: "text" },
        { key: "Marketing Status", type: "text" },
        { key: "Tentative Approval Date", type: "date" },
        { key: "Approval Holder", type: "text" },
      ],
    },

    "Strengths (mg)": {
      allowMultiple: false,
      fields: [
        { key: "Strength", type: "text" },
        { key: "Approval Date", type: "date" },
      ],
    },

    "Newly added Patents": {
      allowMultiple: true, // ✅ multi objects
      fields: [
        { key: "Strength", type: "text" },
        { key: "Patent No", type: "text" },
        { key: "Patent Expiration", type: "date" },
        { key: "Drug Substance", type: "text" },
        { key: "Drug Product", type: "text" },
        { key: "Patent Use Code", type: "text" },
        { key: "Delist Requested", type: "text" },
        { key: "Submission Date", type: "date" },
      ],
    },

    "Delisted patents": {
      allowMultiple: true, // ✅ multi objects
      fields: [
        { key: "Strength", type: "text" },
        { key: "Patent No", type: "text" },
        { key: "Mkt Status", type: "text" },
        { key: "Submission Date", type: "date" },
      ],
    },

    "IPR details (if any)": {
      allowMultiple: true, // ✅ multi objects
      fields: [
        { key: "Proceeding Filing Date", type: "date" },
        { key: "Proceeding Number", type: "text" },
        { key: "Respondent Patent Number", type: "text" },
        { key: "respondentPartyName", type: "text" },
        { key: "Petitioner Party Name", type: "text" },
        { key: "Proceeding Status Category", type: "text" },
        { key: "Decision Date", type: "date" },
        { key: "Termination date", type: "date" },
      ],
    },

    "FDA label": {
      allowMultiple: false,
      fields: [
        { key: "Filepath", type: "file" }, // ✅ PDF upload
        { key: "action date", type: "date" },
      ],
    },
    "FDA product specific BE guidance": {
      allowMultiple: false,
      fields: [
        { key: "Filepath", type: "file" }, // ✅ only file
        { key: "GuidanceDate", type: "date" },    // ✅ date
        { key: "RevisedOrNew", type: "text" },
      ],
    },
    "NDA approval": {
      allowMultiple: false,
      fields: [
        { key: "NDA approval", type: "text" }
      ],
    },
    "New dosage form & new formulation": {
      allowMultiple: false,
      fields: [
        { key: "New dosage form & new formulation", type: "text" }
      ],
    },
    "New Approved Indication": {
      allowMultiple: false,
      fields: [
        { key: "Approval_Date", type: "date" },
        { key: "Active_Ingredient", type: "text" },
        { key: "Route", type: "text" },
        { key: "Dosage_Form", type: "text" },
        { key: "Company_Name", type: "text" },
        { key: "Category", type: "text" },
        { key: "Indication_Details", type: "text" }
      ],
    },
  };

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private lmsDsService: LmsDsManualEntryService
  ) { }

  ngOnInit(): void {
    // init empty
    this.manualForm = this.fb.group({
      rows: this.fb.array([]),
    });

    this.loadWhichFields();
  }

  // ✅ Dropdown GET
  loadWhichFields() {
    this.lmsDsService.getUniqueWhichField().subscribe((res) => {
      this.whichFieldOptions = (res?.data || []).map((x) => ({ label: x, value: x }));
    });
  }

  get rows(): FormArray {
    return this.manualForm.get("rows") as FormArray;
  }

  onWhichFieldChange() {
    if (!this.selectedWhichField) return;

    const def = this.formMap[this.selectedWhichField];
    if (!def) {
      this.toastr.error("This Which Field is not configured yet.", "Error");
      this.currentDef = null;
      this.rows.clear();
      return;
    }

    this.currentDef = def;
    this.rows.clear();

    // create first row always
    this.addRow();
  }

  // ✅ Create a row group from current field definitions
  private buildRowGroup(): FormGroup {
    const optionalFields = ["TE Code", "RLD", "RS", "Submission Date"];

    const g: any = {};
    (this.currentDef?.fields || []).forEach((f) => {
      g[f.key] = optionalFields.includes(f.key)
        ? [null]
        : [null, Validators.required];
    });
    return this.fb.group(g);
  }

  addRow() {
    if (!this.currentDef) return;
    this.rows.push(this.buildRowGroup());
  }

  removeRow(i: number) {
    // keep at least 1 row
    if (this.rows.length <= 1) return;
    this.rows.removeAt(i);
  }

  // ✅ File change handler (FDA label)
  onFileSelected(event: any, rowIndex: number, key: string) {
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    // only pdf validation (optional)
    if (file.type !== "application/pdf") {
      this.toastr.error("Only PDF allowed.", "Error");
      this.rows.at(rowIndex).get(key)?.setValue(null);
      return;
    }

    this.rows.at(rowIndex).get(key)?.setValue(file);
    this.rows.at(rowIndex).get(key)?.markAsTouched();
  }

  // ✅ MAIN SUBMIT
  submitManualEntry() {
    // molecule must be selected because API needs nda/brand/dosage
    const code = this.selectedSubMolecule?.code;
    if (!code) {
      this.toastr.error("Please select NDA from dropdown.", "Error");
      return;
    }

    if (!this.selectedWhichField) {
      this.toastr.error("Please select Unique Which Field.", "Error");
      return;
    }

    if (!this.currentDef) {
      this.toastr.error("Form is not configured.", "Error");
      return;
    }

    if (this.manualForm.invalid) {
      this.manualForm.markAllAsTouched();
      this.toastr.error("Please fill all required fields.", "Error");
      return;
    }

    // ✅ if allowMultiple=false but user still has multiple rows, keep it as is (no harm)
    // For your requirement, only 3 are explicitly multiple; others will have 1 row.

    const newData = this.rows.controls.map((fg) => {
      const obj: any = {};
      this.currentDef!.fields.forEach((f) => {
        const val = fg.get(f.key)?.value;

        if (f.type === "date") {
          // ✅ Submission Date defaults to "N/A" if empty/untouched
          if (f.key === "Submission Date") {
            const formatted = val && val !== "N/A" ? this.formatToMonDdCommaYYYY(val) : "";
            obj[f.key] = formatted ? formatted : "N/A";
          } else if (this.selectedWhichField === "FDA label") {
            obj[f.key] = this.formatToDdMMyyyy(val);
          } else if (this.selectedWhichField === "FDA product specific BE guidance" || this.selectedWhichField === "Strengths (mg)") {
            obj[f.key] = this.formatToMMDDYYYY(val);          // MM/DD/YYYY ✅
          }
          else if (
            this.selectedWhichField === "Approved ANDA filers" ||
            this.selectedWhichField === "Tentatively approved ANDAs" ||
            this.selectedWhichField === "Newly added Patents" ||
            this.selectedWhichField === "Delisted patents" ||
            this.selectedWhichField === "IPR details (if any)" ||
            this.selectedWhichField === "New Approved Indication"
          ) {
            obj[f.key] = this.formatToMonDdCommaYYYY(val);
          }
          // ✅ all other forms keep existing format
          else {
            obj[f.key] = this.formatToDdMonYYYY(val);
          }
        } else if (f.type === "file") {
          // keep file instance (we will send FormData)
          obj[f.key] = val;
        } else {
          const optionalFields = ["TE Code", "RLD", "RS"];
          if (optionalFields.includes(f.key)) {
            obj[f.key] = val ? val : "";
          } else {
            obj[f.key] = val;
          }
        }
      });
      return obj;
    });

    // ✅ payload exactly as you asked (note spelling: whcich_filed)
    const payload = {
      nda_number: String(code.nda_number),
      brand_name: String(code.brand_name),
      dosage: String(code.dosage_forms),
      whcich_filed: this.selectedWhichField, // keep exact key name
      new_data: newData,
      user_email: localStorage.getItem("email") || "",
    };

    // ✅ If any file: must use multipart FormData because of file upload
    const hasFile = this.currentDef.fields.some((x) => x.type === "file");
    if (hasFile) {
      const fd = new FormData();
      fd.append("nda_number", payload.nda_number);
      fd.append("brand_name", payload.brand_name);
      fd.append("dosage", payload.dosage);
      fd.append("whcich_filed", payload.whcich_filed);
      fd.append("user_email", payload.user_email);

      const fileKey = this.currentDef.fields.find(x => x.type === "file")!.key; // "Filepath"
      const file: File = this.rows.at(0).get(fileKey)?.value;

      if (!file) {
        this.toastr.error("Please select PDF file.", "Error");
        return;
      }

      // ✅ ONLY for BE guidance: new_data must be strictly [{ Filepath: "sample.pdf" }]
      if (this.selectedWhichField === "FDA product specific BE guidance") {
        const fileKey = "Filepath";
        const file: File = this.rows.at(0).get(fileKey)?.value;

        if (!file) {
          this.toastr.error("Please select PDF file.", "Error");
          return;
        }

        const gdRaw = this.rows.at(0).get("GuidanceDate")?.value;
        const revisedOrNew = this.rows.at(0).get("RevisedOrNew")?.value;

        const rowObj = {
          Filepath: file.name,
          GuidanceDate: this.formatToMMDDYYYY(gdRaw),
          RevisedOrNew: revisedOrNew
        };

        // ✅ EXACT format you asked
        fd.append("new_data", JSON.stringify([rowObj]));

        // ✅ binary
        fd.append(fileKey, file, file.name);

        this.lmsDsService.submitManualEntry(fd).subscribe(
          () => {
            this.toastr.success("Submitted successfully", "Success");
            //  this.clearFilter();
          },
          (err) => this.toastr.error(err?.error?.error || "Submit failed.", "Error")
        );
        return;
      }



      // ✅ existing behavior for all other file types (FDA label etc.)
      const jsonCopy = payload.new_data.map((x: any) => {
        const copy = { ...x };
        if (copy[fileKey] instanceof File) {
          copy[fileKey] = (copy[fileKey] as File).name;
        }
        return copy;
      });

      fd.append("new_data", JSON.stringify(jsonCopy));
      fd.append(fileKey, file, file.name);

      this.lmsDsService.submitManualEntry(fd).subscribe(
        () => {
          this.toastr.success("Submitted successfully", "Success");
          //  this.clearFilter();
        },
        () => this.toastr.error("Submit failed.", "Error")
      );
      return;
    }


    // ✅ normal JSON post
    this.lmsDsService.submitManualEntry(payload).subscribe(
      () => {
        this.toastr.success("Submitted successfully", "Success");
        // this.clearFilter();
      },
      () => this.toastr.error("Submit failed.", "Error")
    );
  }

  clearFilter() {
    this.selectedWhichField = null;
    this.currentDef = null;
    this.rows.clear();
  }

  // Keep these buttons for same header UI
  searchMolecule() {
    this.submolecules = [];
    this.lmsDsService.getNdaData(this.searchValue).subscribe((ndaValues: any) => {
      if (ndaValues["data"].nda_numbers.length > 0) {
        let dropdownvalues = this.arrayToDropdown(ndaValues["data"].nda_numbers);
        console.log("dropdownnn", dropdownvalues)
        this.submolecules = dropdownvalues;
      }
      console.log("submolecules", this.submolecules);
    });
  }

  arrayToDropdown(InputArray) {
    let DropDownArray = [];
    InputArray.forEach((DropdownValues) => {
      let dropDownObj = {
        name: `${DropdownValues.brand_name},${DropdownValues.nda_number},${DropdownValues.dosage_forms}`,
        code: DropdownValues // store the full object for later use
      };
      DropDownArray.push(dropDownObj);
    });
    return DropDownArray;
  }

  private formatToDdMonYYYY(dateStr: string): string {
    // input: "2025-12-26"
    if (!dateStr) return "";

    const d = new Date(dateStr + "T00:00:00"); // avoid timezone shift
    const dd = String(d.getDate()).padStart(2, "0");

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = months[d.getMonth()];
    const yyyy = d.getFullYear();

    // required: "DD Mon,YYYY"
    return `${dd} ${mon},${yyyy}`;
  }

  private formatToDdMMyyyy(dateStr: string): string {
    // input: "2025-12-29"
    if (!dateStr) return "";

    const d = new Date(dateStr + "T00:00:00"); // avoid timezone shift
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    // required: "dd/MM/yyyy"
    return `${dd}/${mm}/${yyyy}`;
  }

  private formatToMMDDYYYY(dateStr: string): string {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  clearAllManualEntry() {
    // ✅ only clear form values; do NOT clear selectedWhichField / currentDef / selectedSubMolecule
    if (!this.currentDef) return;

    this.rows.clear();
    this.addRow(); // keep one fresh empty row always (works for allowMultiple too)
  }

  private formatToMonDdCommaYYYY(dateStr: string): string {
    // input: "2025-12-23"  => output: "Dec 23, 2025"
    if (!dateStr) return "";

    const d = new Date(dateStr + "T00:00:00"); // avoid timezone shift

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = months[d.getMonth()];
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${mon} ${dd}, ${yyyy}`;
  }

}